import {Alert, FlexRow, Modal} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import type {UpdateCustomFieldDefinitionMutation$data as UpdateCustomFieldDefinitionMutationResponse} from '../../../../mutations/custom-fields/__generated__/UpdateCustomFieldDefinitionMutation.graphql';
import UpdateCustomFieldDefinitionMutation from '../../../../mutations/custom-fields/UpdateCustomFieldDefinitionMutation';
import {ModalProps} from '../../../shared/components/modals/types';
import Util from '../../../shared/util/util';
import {useAvaRefOptions} from '../components/ava-custom-field-dropdown/useAvaRefOptions';
import {CustomFieldDefinitionForm} from '../components/CustomFieldDefinitionForm';
import CustomFieldDetailsSection from '../components/CustomFieldDetailsSection';
import {CustomFieldType} from '../Constants';
import {AlertMessage, CustomFieldDefinition} from '../types';
import type {CustomFieldDefinitionEditModal_company$data as CustomFieldDefinitionEditModal_company} from './__generated__/CustomFieldDefinitionEditModal_company.graphql';
import type {CustomFieldDefinitionEditModal_viewer$data as CustomFieldDefinitionEditModal_viewer} from './__generated__/CustomFieldDefinitionEditModal_viewer.graphql';
import {showCustomFieldDropdownOptionsModal} from './dropdown-options-modal/CustomFieldDropdownOptionsModal';

export interface CustomFieldDefinitionEditModalInput {
	id: string;
	setAlertMessage: (message: AlertMessage) => void;
}

interface CustomFieldDefinitionEditModalInnerProps {
	selectedDefinition: CustomFieldDefinition;
	customFieldDefinitions: CustomFieldDefinition[];
	setAlertMessage: (message: AlertMessage) => void;
}

const CustomFieldDefinitionEditModalInner: React.FC<ModalProps<CustomFieldDefinitionEditModalInnerProps>> = ({
	selectedDefinition,
	customFieldDefinitions,
	setAlertMessage,
	closeModal,
}) => {
	const {formatMessage} = useIntl();

	const otherDefinitions = customFieldDefinitions.filter(cf => cf?.id !== selectedDefinition.id);

	const fieldKey = selectedDefinition.key;
	const entityType = selectedDefinition.entityType;
	const customFieldType = selectedDefinition.type;

	const [displayName, setDisplayName] = useState(selectedDefinition.displayName || '');
	const [readOnly, setReadOnly] = useState(selectedDefinition.readOnly || false);

	const {
		selectedOption: selectedAvaRef,
		setSelectedOption: selectAvaRefOption,
		options: avaRefOptions,
	} = useAvaRefOptions(entityType, otherDefinitions, selectedDefinition.avaRef);

	const update = (onComplete?: (newDefinition: CustomFieldDefinition) => void) => {
		Util.CommitMutation(
			UpdateCustomFieldDefinitionMutation,
			{
				id: selectedDefinition.id,
				displayName,
				readOnly,
				avaRef: selectedAvaRef.value,
			},
			({updateCustomFieldDefinition}: UpdateCustomFieldDefinitionMutationResponse) => {
				const newDefinition = updateCustomFieldDefinition?.customFieldDefinition?.node as CustomFieldDefinition | null;
				setAlertMessage({
					text: formatMessage({id: 'settings.custom-fields.update_alert'}, {name: displayName}),
					type: Alert.TYPE.SUCCESS,
				});
				closeModal();
				newDefinition && onComplete?.({...selectedDefinition, ...newDefinition});
			}
		);
	};

	const isValid = !!displayName;

	const editCustomFieldOptions = (definition: CustomFieldDefinition): void =>
		showCustomFieldDropdownOptionsModal({customFieldDefinition: definition, customFieldId: definition.id, setAlertMessage});

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{'Edit Custom Field'}</Modal.Title>
			<Modal.Content>
				<FlexRow alignItems={'start'} gap={'l'} justifyContent={'space-between'}>
					<CustomFieldDefinitionForm
						entityType={entityType}
						displayName={displayName}
						setDisplayName={setDisplayName}
						fieldKey={fieldKey}
						customFieldType={customFieldType}
						avaRefOptions={avaRefOptions}
						selectedAvaRef={selectedAvaRef}
						selectAvaRefOption={selectAvaRefOption}
						readOnly={readOnly}
						setReadOnly={setReadOnly}
					/>
					<CustomFieldDetailsSection customFieldDefinition={selectedDefinition} />
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton disabled={!isValid} onPress={() => update()} dataCy="button-save">
					{'Save Custom Field'}
				</Modal.PrimaryFooterButton>
				{customFieldType === CustomFieldType.DROPDOWN && (
					<Modal.SecondaryFooterButton disabled={!displayName} onPress={() => update(editCustomFieldOptions)}>
						<FormattedMessage id={'settings.custom-fields.dropdown_entity_save_button'} />
					</Modal.SecondaryFooterButton>
				)}
			</Modal.Footer>
		</Modal>
	);
};

type CustomFieldDefinitionEditModalProps = ModalProps<
	{
		viewer: CustomFieldDefinitionEditModal_viewer;
		company: CustomFieldDefinitionEditModal_company;
	} & CustomFieldDefinitionEditModalInput
>;

const CustomFieldDefinitionEditModalInternal: React.FC<CustomFieldDefinitionEditModalProps> = ({
	viewer,
	company,
	setAlertMessage,
	closeModal,
}) => {
	// TODO: fix this type coersion;
	const customFieldDefinitions = (company?.customFieldDefinitionsV2?.edges?.map(edge => edge?.node) ||
		[]) as CustomFieldDefinition[];

	const selectedDefinition = viewer.customFieldDefinition as CustomFieldDefinition;

	return selectedDefinition ? (
		<CustomFieldDefinitionEditModalInner
			selectedDefinition={selectedDefinition}
			customFieldDefinitions={customFieldDefinitions}
			setAlertMessage={setAlertMessage}
			closeModal={closeModal}
		/>
	) : null;
};

export const CustomFieldDefinitionEditModal = createFragmentContainer(CustomFieldDefinitionEditModalInternal, {
	company: graphql`
		fragment CustomFieldDefinitionEditModal_company on Company {
			id
			customFieldDefinitionsV2(first: 1000) @connection(key: "Company_customFieldDefinitionsV2") {
				edges {
					node {
						id
						key
						type
						displayName
						entityType
						avaRef
					}
				}
			}
		}
	`,
	viewer: graphql`
		fragment CustomFieldDefinitionEditModal_viewer on Viewer @argumentDefinitions(id: {type: "ID"}) {
			customFieldDefinition(id: $id) {
				id
				key
				type
				displayName
				entityType
				avaRef
				... on TextCustomField {
					readOnly
				}
				... on IntegerCustomField {
					readOnly
				}
				... on DecimalCustomField {
					readOnly
				}
				... on DateCustomField {
					readOnly
				}
			}
		}
	`,
});
